import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import { Box, Checkbox, FormControlLabel } from "@mui/material";
import { useTranslation } from "react-i18next";

import { BModal } from "components";
import { useAsterController } from "context";
import { actionAppDashboardGet, actionAppFirstLoadGet, actionEditAccount } from "context/action";
import { VButton, VText } from "form";
import { isOnline, probeOnline } from "offline/network";
import { hydrateSyncQueueFromBackup } from "offline/queue";
import { flushSyncQueue } from "offline/sync";
import SafeHtml from "pages/common/SafeHtml";

/**
 * Requires a JWT, loads the dashboard, and prompts for terms of service when needed.
 *
 * @param {{ children: React.ReactNode }} props
 */
export default function AuthProtector(props) {
  const { children } = props;
  const { t } = useTranslation("common");

  const [controller, dispatch] = useAsterController();
  const { user, term, loadedDashboard, sessionHydrated, restoredFromSession } = controller;

  const [open, setOpen] = useState(false);
  const [checked, setChecked] = useState(false);
  const hasToken = !!(localStorage.getItem("jwt-ghost") || localStorage.getItem("jwt"));

  useEffect(() => {
    if (!hasToken) {
      const authBase = String(process.env.REACT_APP_AUTH_ENDPOINT || "").replace(/\/$/, "");
      const next = encodeURIComponent(window.location.href);
      window.location.replace(`${authBase}/login?next=${next}`);
      return;
    }
    if (!sessionHydrated) return;

    let cancelled = false;
    (async () => {
      await hydrateSyncQueueFromBackup().catch(() => []);
      const reachable = isOnline() && (await probeOnline());
      if (reachable) {
        await flushSyncQueue();
        if (cancelled) return;
        dispatch({ type: "SET_FIRST_LOAD_PENDING", value: true });
        try {
          await actionAppFirstLoadGet(dispatch);
        } catch {
          if (!cancelled) actionAppDashboardGet(dispatch);
        } finally {
          if (!cancelled) dispatch({ type: "SET_FIRST_LOAD_PENDING", value: false });
        }
        return;
      }
      dispatch({ type: "SET_FIRST_LOAD_PENDING", value: false });
      if (restoredFromSession) {
        dispatch({ type: "LOADED_APP_DASHBOARD", value: true });
        return;
      }
      if (!cancelled) actionAppDashboardGet(dispatch);
    })();

    return () => {
      cancelled = true;
    };
  }, [dispatch, hasToken, sessionHydrated, restoredFromSession]);
  useEffect(() => !user.term_accepted && loadedDashboard && setOpen(true), [user.term_accepted, loadedDashboard]);

  if (!hasToken) return null;

  /**
   * Accepts the terms of service and persists the flag on the learner account.
   *
   * @returns {void}
   */
  const onSubmit = () => {
    setOpen(false);
    actionEditAccount(dispatch, { term_accepted: true }, t, false);
  };

  return (
    <>
      {children}

      <BModal open={open} width="90vw" height="fit-content" className="w-full max-w-xl max-h-150 p-8">
        <VText className="font-bold flex items-center color-primary text-xl" color="custom">
          <Icon icon="fluent:shield-keyhole-20-filled" />
          &nbsp;
          {t("Terms of service")}
        </VText>
        <Box className="h-80 overflow-y-auto v-light-scrollbar my-4">
          <SafeHtml className="mb-2" html={term.content} />
        </Box>
        <Box>
          <FormControlLabel control={<Checkbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />} label={t("I agree")} />
        </Box>
        <Box className="flex w-full justify-center">
          <VButton variant="contained" color="primary" onClick={onSubmit} disabled={!checked}>
            {t("Submit")}
          </VButton>
        </Box>
      </BModal>
    </>
  );
}

AuthProtector.propTypes = {
  children: PropTypes.any,
};
